/**
 * compactDdlCard.nameOnly — object-name resolver for the "name only" DDL
 * kinds `extractDdlStructure` has no structural arm for: view / materialized
 * view / sequence / schema / security principal (login/user/role) / database
 * / package. Each delegates to the matching VDE `parseParked*Statement`
 * scanner (§2.9 S13, plan-9 decision 2) — no new parsing.
 *
 * Split out of `compactDdlCard.ts` to keep that module focused on
 * classification + top-level dispatch.
 */
import type { DdlKind } from '../../visual-ddl-editor/inline/classifyDdlAtCursor';
import { parseParkedDatabaseStatement } from '../../visual-ddl-editor/inline/parseParkedDatabaseStatement';
import { parseParkedPackageStatement } from '../../visual-ddl-editor/inline/parseParkedPackageStatement';
import { parseParkedSchemaStatement } from '../../visual-ddl-editor/inline/parseParkedSchemaStatement';
import { parseParkedSecurityStatement } from '../../visual-ddl-editor/inline/parseParkedSecurityStatement';
import { parseParkedSequenceStatement } from '../../visual-ddl-editor/inline/parseParkedSequenceStatement';
import { parseParkedViewStatement } from '../../visual-ddl-editor/inline/parseParkedViewStatement';

/** Display object name for a "name only" `DdlKind` via the matching parked
 *  parser, or `null` for any `ddlKind` this module doesn't own (the caller
 *  only invokes it for the name-only arms). */
export function nameOnlyObjectName(ddlKind: DdlKind, statementText: string): string | null {
  switch (ddlKind) {
    case 'create-view':
    case 'alter-view':
    case 'create-materialized-view':
    case 'alter-materialized-view':
      return parseParkedViewStatement(statementText)?.name.raw ?? null;
    case 'create-sequence':
    case 'alter-sequence':
      return parseParkedSequenceStatement(statementText)?.name.raw ?? null;
    case 'create-schema':
      return parseParkedSchemaStatement(statementText)?.name ?? null;
    case 'create-login':
    case 'alter-login':
    case 'create-user':
    case 'alter-user':
    case 'create-role':
    case 'alter-role':
      return parseParkedSecurityStatement(statementText)?.name ?? null;
    case 'create-database':
    case 'alter-database':
      return parseParkedDatabaseStatement(statementText)?.name ?? null;
    case 'create-package':
    case 'create-package-body':
    case 'alter-package':
      return parseParkedPackageStatement(statementText)?.name.raw ?? null;
    default:
      return null;
  }
}
